import { useState, useEffect } from 'react';
import { collection, getDocs, query } from 'firebase/firestore';
import { db } from '../firebase';
import { Event } from '../types/Event';
import { useAuth } from '../contexts/AuthContext';
import { User } from '../types/User';
import { Subscription } from '../types/subscription';

export interface EventStats {
    paidCount: number;
    unpaidCount: number;
    unpaidNames: string[]; // List of names/nicknames of unpaid participants
    unpaidParticipantIds: string[];
    totalParticipants: number;
    capacity: number;
}

export const useAdminEventStats = (events: Event[]) => {
    const { isAdmin } = useAuth();
    const [stats, setStats] = useState<Record<string, EventStats>>({});
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!isAdmin || events.length === 0) return;

        const fetchStats = async () => {
            setLoading(true);
            try {
                // 1. Fetch Users (to get names)
                const usersSnapshot = await getDocs(collection(db, 'users'));
                const usersData = usersSnapshot.docs.reduce((acc, doc) => {
                    acc[doc.id] = { id: doc.id, ...doc.data() } as User;
                    return acc;
                }, {} as Record<string, User>);

                // 2. Fetch Subscriptions
                const subsSnapshot = await getDocs(collection(db, 'subscriptions'));
                const subscriptionsData = subsSnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                })) as Subscription[];

                // 3. Calculate Membership Status per Year
                const MEMBERSHIP_THRESHOLD = 10;
                const yearlyPaymentsByUser: Record<string, Record<number, number>> = {};
                const yearlyMembershipStatus: Record<number, string[]> = {};

                subscriptionsData.forEach(sub => {
                    const userId = sub.userId;
                    let paymentDate;
                    if (typeof sub.paymentDate === 'string') {
                        paymentDate = new Date(sub.paymentDate);
                    } else if (sub.paymentDate && (sub.paymentDate as any).seconds) {
                        paymentDate = new Date((sub.paymentDate as any).seconds * 1000);
                    } else {
                        paymentDate = new Date();
                    }
                    const year = paymentDate.getFullYear();
                    const amount = Number(sub.amount) || 0;

                    if (!yearlyPaymentsByUser[userId]) yearlyPaymentsByUser[userId] = {};
                    if (!yearlyPaymentsByUser[userId][year]) yearlyPaymentsByUser[userId][year] = 0;
                    yearlyPaymentsByUser[userId][year] += amount;
                });

                Object.entries(yearlyPaymentsByUser).forEach(([userId, yearlyAmounts]) => {
                    Object.entries(yearlyAmounts).forEach(([year, totalAmount]) => {
                        const yearNum = parseInt(year);
                        if (totalAmount >= MEMBERSHIP_THRESHOLD) {
                            if (!yearlyMembershipStatus[yearNum]) yearlyMembershipStatus[yearNum] = [];
                            if (!yearlyMembershipStatus[yearNum].includes(userId)) {
                                yearlyMembershipStatus[yearNum].push(userId);
                            }
                        }
                    });
                });

                // 4. Compute Stats per Event
                const newStats: Record<string, EventStats> = {};

                events.forEach(event => {
                    if (!event.id) return;

                    const eventYear = new Date(event.startDate).getFullYear();
                    const participants = event.participants || [];
                    const isWanEvent = event.isWanEvent || event.zone === 'wan_online';
                    let paidParticipants: string[] = [];

                    if (isWanEvent) {
                        // WAN events are free
                        paidParticipants = [...participants];
                    } else {
                        // Logic for Paid:
                        // 1. Annual sub for this year
                        const annualSubs = subscriptionsData.filter(sub =>
                            sub.type === 'annual' &&
                            new Date(sub.paymentDate).getFullYear() === eventYear
                        ).map(s => s.userId);

                        // 2. Event specific sub
                        const eventSubs = subscriptionsData.filter(sub =>
                            sub.type === 'event' &&
                            (sub as any).eventId === event.id
                        ).map(s => s.userId);

                        // 3. Membership threshold reached
                        const members = yearlyMembershipStatus[eventYear] || [];

                        // Union
                        paidParticipants = participants.filter(pid =>
                            annualSubs.includes(pid) ||
                            eventSubs.includes(pid) ||
                            members.includes(pid)
                        );
                    }

                    const unpaidParticipants = participants.filter(pid => !paidParticipants.includes(pid));
                    const unpaidNames = unpaidParticipants.map(pid => {
                        const u = usersData[pid];
                        return u ? (u.nickname || `${u.firstName} ${u.lastName}`) : 'Inconnu';
                    });

                    newStats[event.id] = {
                        paidCount: paidParticipants.length,
                        unpaidCount: unpaidParticipants.length,
                        unpaidNames,
                        unpaidParticipantIds: unpaidParticipants,
                        totalParticipants: participants.length,
                        capacity: event.maxParticipants
                    };
                });

                setStats(newStats);

            } catch (error) {
                console.error("Error computing admin stats:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchStats();

    }, [isAdmin, events]); // Re-run if events list changes (e.g. initial load)

    return { stats, loading };
};
